import {
  CardActionArea,
  CircularProgress,
  Divider,
  useMediaQuery,
  Badge,
  Chip,
  Grid,
  Box,
  Card,
  CardContent,
  CardMedia,
  SvgIcon,
  Button,
  Typography,
  Stack,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ArrowBackIos as ArrowBackIosIcon,
  TouchApp as TouchAppIcon,
  Spa as SpaIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  ZoomOutMap as ZoomOutMapIcon,
  CalendarMonth as CalendarMonthIcon,
} from "@mui/icons-material";

import getLPTheme from "../getLPTheme";

import App from "./App";

import { ReactComponent as FilterIcon } from "../images/filter_icon.svg";

import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";

import { useContext, useEffect, useState, useRef } from "react";

import { AuthContext } from "../index";
import { GlobalDataContext } from "../index";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";

import "./MainScreen.css";

import ItemsList from "./ItemsList";
import ItemsListV2 from "./ItemsListV2";
import { getImage, getImageV2 } from "../utils/getImage";
import { extractSubway, groupSubway } from "../utils/strings";

import {
  getHourMinutes,
  getDay,
  today,
  startOfDay,
  endOfDay,
} from "../utils/dateTime";
import { createTheme } from "@mui/material/styles";
import { default as ServiceConfirm } from "./InfoSmall";
import { default as ServiceConfirmStatus } from "./InfoSmall";
import { default as RedirectToAuth } from "./InfoSmall";
import { default as FilterInfoV2 } from "./InfoV2";
import { default as Features } from "./Features";
import { fillById as fillByIdUtil } from "../utils/lists";
import SwipableImageCarousel from "./SwipableImageCarousel";
import _ from "lodash";

function MainScreen() {
  const { filters, setFilters } = useContext(GlobalDataContext);

  const { id, userId, phoneNumber, onLogin, onLogout } =
    useContext(AuthContext);

  const _getValueFromFilter = (type, filters) => {
    if (!filters || !filters.filter((f) => f.type == type).length) {
      return null;
    }

    return filters.filter((f) => f.type == type)[0].value;
  };

  const [services, setServices] = useState({
    isLoaded: false,
    data: [],
    grouped: [],
  });
  const [locations, setLocations] = useState({
    isLoaded: false,
    data: [],
  });
  const [serviceTemplates, setServiceTemplates] = useState({
    isLoaded: false,
    data: [],
  });
  const DEFAULT_FILTER_INFO_STATE = {
    open: false,
    filters: [...filters],
    calendar: {
      open: false,
    },
  };
  const [filterInfo, setFilterInfo] = useState(DEFAULT_FILTER_INFO_STATE);

  const DEFAULT_SERVICE_CONFIRM_STATE = {
    open: false,
    inProgress: false,
    text: "",
    serviceId: "",
  };
  const [serviceConfirm, setServiceConfirm] = useState(
    DEFAULT_SERVICE_CONFIRM_STATE,
  );

  const DEFAULT_SERVICE_CONFIRM_STATUS_STATE = {
    open: false,
    isError: false,
  };

  const DEFAULT_REDIRECT_TO_AUTH_STATE = {
    open: false,
  };

  const [serviceConfirmStatus, setServiceConfirmStatus] = useState(
    DEFAULT_SERVICE_CONFIRM_STATUS_STATE,
  );
  const [redirectToAuth, setRedirectToAuth] = useState(
    DEFAULT_REDIRECT_TO_AUTH_STATE,
  );

  const groupServicesByServiceTemplate = (
    funcServices = [],
    fundServiceTemplates = [],
    funcLocations = [],
  ) => {
    const list = funcServices.reduce((acc, curr) => {
      const idx = acc.findIndex(
        (it) => it.serviceTemplate.id === curr.serviceTemplate.id,
      );

      if (idx != -1) {
        if (curr?.features) {
          acc[idx].features.push(curr?.features);
        }

        const dateIdx = acc[idx].dates.findIndex(
          (d) => d?.date == getDay(curr?.timeFrame?.from),
        );

        if (dateIdx != -1) {
          const locationIdx = acc[idx].dates[dateIdx].locations.findIndex(
            (l) => l.id == curr.location.id,
          );

          if (locationIdx != -1) {
            acc[idx].dates[dateIdx].locations[locationIdx].timeFrames.push(
              curr.timeFrame,
            );
          } else {
            acc[idx].dates[dateIdx].locations.push({
              ...fillByIdUtil(curr.location.id, funcLocations),
              timeFrames: [curr.timeFrame],
            });
          }
        } else {
          acc[idx].dates.push({
            date: getDay(curr?.timeFrame?.from),
            locations: [
              {
                ...fillByIdUtil(curr.location.id, funcLocations),
                timeFrames: [curr.timeFrame],
              },
            ],
          });
        }
      } else {
        acc.push({
          serviceTemplate: fillByIdUtil(
            curr.serviceTemplate.id,
            fundServiceTemplates,
          ),
          features: curr?.features ? [curr.features] : [],
          dates: [
            {
              date: getDay(curr?.timeFrame?.from),
              locations: [
                {
                  ...fillByIdUtil(curr.location.id, funcLocations),
                  timeFrames: [curr.timeFrame],
                },
              ],
            },
          ],
        });
      }

      return acc;
    }, []);

    return list;
  };

  async function loadLocations() {
    let url = `/api/v1/locations`;
    try {
      const resp = await axios.get(url);
      setAllLocations({
        isLoaded: true,
        data: resp.data,
      });
      setLocations({
        isLoaded: true,
        data: resp.data,
      });
      return resp.data;
    } catch (err) {
      throw err;
    }
  }

  async function loadServiceTemplates() {
    let url = `/api/v1/services/templates`;
    try {
      const resp = await axios.get(url);
      setServiceTemplates({
        isLoaded: true,
        data: resp.data,
      });
      return resp.data;
    } catch (err) {
      throw err;
    }
  }

  async function loadServices() {
    const [serviceTemplates, locations] = await Promise.all([
      loadServiceTemplates(),
      loadLocations(),
    ]);

    try {
      const resp = await axios.post(`/api/v1/services/get`, {
        timeFrame: {
          from: _getValueFromFilter("timeFrameFrom", filters),
          to: _getValueFromFilter("timeFrameTo", filters),
        },
      });

      setServices({
        isLoaded: true,
        data: resp.data,
      });
    } catch (err) {
      //noop
    }
  }

  useEffect(() => {
    if (services.isLoaded) {
      return;
    }

    loadServices();
  }, [services.isLoaded]);

  const _handleFilterModalOpen = () => {
    //todo change
    setFilterInfo((prevState) => ({
      ...prevState,
      open: true,
    }));
  };

  const _handleFilterModalClose = () => {
    setFilterInfo((prevState) => ({
      ...prevState,
      open: false,
    }));
  };

  const _handleLocationTimeClick = (
    serviceTemplateId,
    locationId,
    timeFrameFrom,
  ) => {
    if (!userId) {
      setRedirectToAuth((prevState) => ({ ...prevState, open: true }));
      return;
    }

    const service = services?.data?.filter(
      (service) =>
        service?.serviceTemplate?.id == serviceTemplateId &&
        service?.location?.id == locationId &&
        service?.timeFrame?.from == timeFrameFrom,
    )[0];

    setServiceConfirm({
      // open: true,
      serviceId: service?.id,
      text: `
            ${fillByIdUtil(service?.serviceTemplate?.id, serviceTemplates?.data ?? [])?.title}, 
            ${getDay(service?.timeFrame?.from)}, c  
            ${getHourMinutes(service?.timeFrame?.from)} по  
            ${getHourMinutes(service?.timeFrame?.to)} \n
            по адресу: ${fillByIdUtil(service?.location?.id, locations?.data)?.address}
            `,
      customizations: service?.customizations,
    });
  };

  const _handleServiceBook = async ({ serviceId, customizations }) => {
    if (!serviceId) {
      setServiceConfirm(DEFAULT_SERVICE_CONFIRM_STATE);
      setServiceConfirmStatus({ open: true, isError: true });
    }

    try {
      setServiceConfirm((prevState) => ({ ...prevState, inProgress: true }));

      const resp = await axios.put(`/api/v1/services`, {
        id: serviceId,
        client: {
          id: id,
        },
        customizations: customizations,
      });

      setServiceConfirm(DEFAULT_SERVICE_CONFIRM_STATE);
      setServiceConfirmStatus({ open: true, isError: false });
    } catch (err) {
      setServiceConfirm(DEFAULT_SERVICE_CONFIRM_STATE);
      setServiceConfirmStatus({ open: true, isError: true });
    }
  };

  // todo finished here
  const _getCentralChip = () => {
    const from = getDay(_getValueFromFilter("timeFrameFrom", filters));
    const to = getDay(_getValueFromFilter("timeFrameTo", filters));

    const element = (
      <Stack
        sx={{
          p: 2,
          flex: 1,
          flexGrow: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        display={"column"}
      >
        <Box
          sx={{
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
            width: "100%",
          }}
        >
          {getDay(today()) !=
            getDay(_getValueFromFilter("timeFrameFrom", filters)) &&
          getDay(_getValueFromFilter("timeFrameFrom", filters)) ==
            getDay(_getValueFromFilter("timeFrameTo", filters)) ? (
            <Box
              sx={{
                width: "24px",
                borderRadius: "50%",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                plusDaysToDate(-1);
              }}
            >
              <ArrowBackIosIcon
                fontSize={"small"}
                sx={{
                  color: "gray",
                  ml: 1,
                  display: "block",
                }}
              />
            </Box>
          ) : null}
          <Typography fontSize={"18px"} sx={{ color: "gray" }}>
            <CalendarMonthIcon
              sx={{ fontSize: "12px", fontWeight: "bold", pr: "2px" }}
            />
            {getDay(_getValueFromFilter("timeFrameFrom", filters))}
            {from != to ? <>&nbsp;&ndash;&nbsp;</> : null}
            {from != to
              ? getDay(_getValueFromFilter("timeFrameTo", filters))
              : null}
          </Typography>
          {getDay(_getValueFromFilter("timeFrameFrom", filters)) ==
          getDay(_getValueFromFilter("timeFrameTo", filters)) ? (
            <Box
              sx={{
                width: "24px",
                borderRadius: "50%",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                plusDaysToDate(1);
              }}
            >
              <ArrowForwardIosIcon
                fontSize={"small"}
                sx={{
                  color: "gray",
                  mr: 1,
                  display: "block",
                }}
              />
            </Box>
          ) : null}
        </Box>
      </Stack>
    );
    // } else if (dateService) {
    //     element = (<Stack sx={{alignItems: 'center', display: 'flex'}} display={'column'}>
    //             <Box sx={{flex: 1}}>
    //                 <Typography fontSize={'16px'} align={'center'}>
    //                     {getValueFromFilterReadable('service')}
    //                 </Typography>
    //             </Box>
    //             <Box sx={{flex: 1}}>
    //                 <Typography fontSize={'12px'} sx={{color: 'gray', justifyContent: 'center', alignItems: 'center'}} >
    //                     <CalendarMonthIcon sx={{fontSize: '10px', fontWeight: 'bold', pr: '2px'}}/>
    //                     {getValueFromFilterReadable('date')}
    //                 </Typography>
    //             </Box>
    //         </Stack>
    //     )
    // } else if (dateServiceLocation) {
    //     element = (<Stack sx={{alignItems: 'center', display: 'flex'}} display={'column'}>
    //         <Box sx={{flex: 1}}>
    //             <Typography fontSize={'16px'} align={'center'}>
    //                 {getValueFromFilterReadable('service')}
    //             </Typography>
    //         </Box>
    //         <Box sx={{flex: 1}}>
    //             <Typography fontSize={'12px'} sx={{color: 'gray', justifyContent: 'center', alignItems: 'center'}} >
    //                 <CalendarMonthIcon sx={{fontSize: '10px', fontWeight: 'bold', pr: '2px'}}/>
    //                 { getValueFromFilterReadable('date')}
    //                 <span>&nbsp;&middot;&nbsp;</span>
    //                 { getValueFromFilterReadable('time')}
    //                 <span>&nbsp;&middot;&nbsp;</span>
    //                 { getValueFromFilterReadable('location') }
    //             </Typography>
    //         </Box>
    //     </Stack>)
    // }

    return (
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          borderRadius: "30px",
          border: "1px solid #ddd",
          justifyContent: "center",
          alignItems: "center",
          p: "5px",
          height: "40px",
          boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
          width: "100%",
        }}
        onClick={() => {
          setFilterInfo((prevState) => ({ ...prevState, open: true }));
        }}
      >
        {element}
      </Box>
    );
  };

  const handleDatePickV2 = (newDate, type) => {
    setFilterInfo((prevState) => {
      let nextFilters = [...(prevState?.filters ?? [])];

      if (type == "from") {
        const ixFrom = nextFilters.findIndex(
          (fltr) => fltr.type == "timeFrameFrom",
        );
        if (ixFrom != -1) {
          nextFilters.splice(ixFrom, 1);
        }

        nextFilters.push({
          type: "timeFrameFrom",
          value: startOfDay(newDate / 1000),
        });
      }

      if (type == "to") {
        const ixTo = nextFilters.findIndex(
          (fltr) => fltr.type == "timeFrameTo",
        );
        if (ixTo != -1) {
          nextFilters.splice(ixTo, 1);
        }

        nextFilters.push({
          type: "timeFrameTo",
          value: endOfDay(newDate / 1000),
        });
      }

      return {
        ...prevState,
        calendar: { ...prevState?.calendar, openFrom: false, openTo: false },
        filters: [...nextFilters],
      };
    });
  };

  const plusDaysToDate = (day) => {
    day = parseInt(day);
    setFilters((prevState) => {
      const nextFilters = [...filters];
      let newDateFrom = 0;
      const ixFrom = nextFilters.findIndex(
        (fltr) => fltr.type == "timeFrameFrom",
      );
      if (ixFrom != -1) {
        newDateFrom = new Date(parseInt(nextFilters[ixFrom].value * 1000));
        newDateFrom.setDate(newDateFrom.getDate() + day);

        nextFilters.splice(ixFrom, 1);
      }

      let newDateTo = 0;
      const ixTo = nextFilters.findIndex((fltr) => fltr.type == "timeFrameTo");
      if (ixTo != -1) {
        newDateTo = new Date(parseInt(nextFilters[ixTo].value * 1000));
        newDateTo.setDate(newDateTo.getDate() + day);

        nextFilters.splice(ixTo, 1);
      }

      return [
        ...nextFilters,
        { type: "timeFrameFrom", value: startOfDay(newDateFrom / 1000) },
        { type: "timeFrameTo", value: endOfDay(newDateTo / 1000) },
      ];
    });
  };

  useEffect(() => {
    setServices((prevState) => ({ ...prevState, isLoaded: false }));
    setFilterInfo(DEFAULT_FILTER_INFO_STATE);
  }, [filters]);

  const handleDiscountPickV2 = () => {
    setFilterInfo((prevState) => {
      const nextFilters = [...(prevState?.filters ?? [])];
      const ix = nextFilters.findIndex((fltr) => fltr.type == "discount");
      if (ix != -1) {
        const currValue = nextFilters[ix].value;
        nextFilters.splice(ix, 1);
        nextFilters.push({ type: "discount", value: !currValue });
      } else {
        nextFilters.push({ type: "discount", value: true });
      }

      return {
        ...prevState,
        filters: [...nextFilters],
      };
    });
  };

  const isPickedDateTime = (locationItem, timeFrame) => {
    const filteredServices = services?.data?.filter(
      (service) => service?.id == serviceConfirm?.serviceId,
    );
    if (
      filteredServices?.length &&
      filteredServices[0].serviceTemplate?.id ==
        serviceDetailed?.serviceTemplate?.id &&
      locationItem?.id == filteredServices[0]?.location?.id &&
      timeFrame?.from == filteredServices[0]?.timeFrame?.from
    ) {
      return true;
    }
    return false;
  };

  const [serviceDetailed, setServiceDetailed] = useState(null);
  useEffect(() => {
    if (serviceDetailed == null) {
      setServiceConfirm(DEFAULT_SERVICE_CONFIRM_STATUS_STATE);
    }
  }, [serviceDetailed]);

  const [locationDetailed, setLocationDetailed] = useState(null);
  const [mapDetailed, setMapDetailed] = useState(null);
  const [serviceBook, setServiceBook] = useState({ isLoaded: true });
  const datePlaceSection = useRef(null);

  const ret = (
    <App
      top={
        <Stack
          sx={{
            backgroundColor: "#fcfcfc",
            boxShadow:
              "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
            isolation: "isolate",
          }}
        >
          <Stack
            spacing={3}
            direction="row"
            sx={{
              p: 2,
              display: "flex",
              // flexGrow: 0,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Badge
                badgeContent={
                  filters.filter((it) => it.type != "timeFrameTo").length
                }
                color={"primary"}
                onClick={_handleFilterModalOpen}
                sx={{
                  "&:hover": { cursor: "pointer" },
                  borderRadius: "15px",
                  p: "2px",
                  border: "1px solid gray",
                }}
              >
                <FilterListIcon sx={{ color: "gray" }} />
                {/*<SvgIcon component={FilterIcon} inheritViewBox fontSize="15px" sx={{ verticalAlign: "middle", color: 'black' }} />*/}
              </Badge>
            </Box>
            {_getCentralChip()}
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{
                flex: 1,
                flexGrow: 1,
                display: "flex",
                justifyContent: "flex-end",
              }}
              onClick={() => {
                setFilterInfo((prevState) => ({
                  ...prevState,
                  categoryDropdown: { open: !prevState.categoryDropdown.open },
                }));
              }}
            >
              {/* https://medium.com/@limjoshen/gradient-borders-with-pure-css-db55c83c840b*/}
            </Stack>
          </Stack>
        </Stack>
      }
    >
      <Grid container>
        <Grid item md={3} lg={2} xs={12}>
          <Box>
            <ItemsListV2
              itemsLoading={!services.isLoaded}
              closeDetailed={() => {
                setServiceDetailed(null);
              }}
              items={
                services?.data?.length ? (
                  groupServicesByServiceTemplate(
                    services.data,
                    serviceTemplates.data,
                    locations.data,
                  ).map((item) => (
                    <Card
                      elevation={1}
                      sx={{
                        display: "flex",
                        border: "none",
                        "&:focus": { outline: "none" },
                        p: 1,
                      }}
                      onClick={() => {
                        setServiceDetailed(item);
                      }}
                    >
                      <CardActionArea
                        disableRipple
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                          alignItems: "flex-start",
                        }}
                        onClick={() => {
                          setServiceDetailed(item);
                        }}
                      >
                        <Box
                          sx={{
                            borderRadius: "15px",
                            overflow: "hidden",
                          }}
                        >
                          <SwipableImageCarousel
                            images={item?.serviceTemplate?.images || []}
                            getImage={getImage}
                            imageUrls={item?.serviceTemplate?.images?.map(
                              (it) => getImageV2(it?.id),
                            )}
                          />
                        </Box>
                        <CardContent
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            p: 0,
                            pt: 1,
                            // height: '100%',
                            marginBottom: 0,
                            width: "100%",
                          }}
                        >
                          <Stack
                            sx={{
                              height: "100%",
                              justifyContent: "space-between",
                            }}
                          >
                            <Stack>
                              <Stack
                                direction={"row"}
                                justifyContent={"space-between"}
                              >
                                <Typography
                                  sx={{ fontSize: "15px", fontWeight: "bold" }}
                                  align={"left"}
                                >
                                  {" "}
                                  {item?.serviceTemplate?.title}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "14px" }}
                                  align={"left"}
                                >
                                  {" "}
                                  {item?.serviceTemplate?.price}{" "}
                                  <span>&#8381;</span>
                                </Typography>
                              </Stack>

                              <Typography
                                sx={{ fontSize: "10px", color: "gray" }}
                                align={"left"}
                              >
                                {item?.serviceTemplate?.descriptionShort}
                              </Typography>
                              <Typography
                                sx={{ mt: 1, fontSize: "10px" }}
                                align={"left"}
                              >
                                {/* add svg icon with mm.svg here*/}
                                {/*  { _.map(groupSubway(_.flatMap(item?.dates, date => date?.locations?.map(loc => loc?.address))), lobj => lobj.icon }*/}
                                {
                                  extractSubway(
                                    _.head(_.head(item?.dates)?.locations)
                                      ?.address,
                                  )?.icon
                                }
                                <span> </span>
                                {
                                  extractSubway(
                                    _.head(_.head(item?.dates)?.locations)
                                      ?.address,
                                  )?.fullName
                                }
                              </Typography>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))
                ) : (
                  <Typography sx={{ color: "gray", fontSize: "14px" }}>
                    Все массажи на эти даты уже разобрали
                  </Typography>
                )
              }
              itemDetailed={
                serviceDetailed ? (
                  <Card
                    elevation={1}
                    sx={{
                      display: "flex",
                      height: "100dvh",
                      backgroundColor: "#fff",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        justifyContent: "space-between",
                        overflowY: "auto",
                        height: "100%",
                      }}
                    >
                      <SwipableImageCarousel
                        images={serviceDetailed?.serviceTemplate?.images || []}
                        aspectRatio={"4/3"}
                        getImage={getImage}
                        imageUrls={serviceDetailed?.serviceTemplate?.images?.map(
                          (it) => getImageV2(it?.id),
                        )}
                        navigation={"fraction"}
                      />
                      <CardContent
                        sx={{
                          position: "relative",
                          marginTop: "-25px",
                          backgroundColor: "#fff",
                          borderTopLeftRadius: "25px",
                          borderTopRightRadius: "25px",
                        }}
                      >
                        <Typography
                          align="center"
                          sx={{ pb: 2, fontSize: "24px" }}
                        >
                          {" "}
                          {serviceDetailed?.serviceTemplate?.title}{" "}
                        </Typography>
                        <Typography
                          align="center"
                          sx={{ fontSize: "12px", color: "gray" }}
                        >
                          {" "}
                          {serviceDetailed?.serviceTemplate?.description}{" "}
                        </Typography>
                        {serviceDetailed?.features?.length ? (
                          <Features
                            sxExtra={{ mt: 4 }}
                            features={serviceDetailed?.features}
                          />
                        ) : null}
                        <Typography
                          ref={datePlaceSection}
                          sx={{ pt: 6, fontSize: "16px", fontWeight: "bold" }}
                          align={"left"}
                        >
                          Место и время
                        </Typography>
                        <ItemsListV2
                          items={serviceDetailed?.dates
                            .sort(
                              (dateItemFirst, dateItemSecond) =>
                                dateItemFirst?.locations[0].timeFrame?.from -
                                dateItemSecond.timeFrame?.from,
                            )
                            .map((dateItem) => (
                              <>
                                <Typography
                                  fontSize={"16px"}
                                  color={"gray"}
                                  align={"left"}
                                >
                                  {dateItem?.date}
                                </Typography>
                                {dateItem?.locations.map((locationItem) => (
                                  <Box
                                    sx={{
                                      borderRadius: "24px",
                                      boxShadow:
                                        "rgba(0, 0, 0, 0.1) 0px 4px 12px",
                                    }}
                                    onClick={() => {
                                      setLocationDetailed(locationItem);
                                    }}
                                  >
                                    <CardActionArea
                                      disableRipple
                                      sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          position: "relative",
                                          width: 100,
                                          height: 100,
                                          m: "2px",
                                          mr: 1,
                                        }}
                                      >
                                        <CardMedia
                                          component="img"
                                          sx={{
                                            width: 100,
                                            height: 100,
                                            borderRadius: "24px",
                                          }}
                                          // image={  getImage(locationItem?.images?.map(im => im.id)) }
                                          image={getImageV2(
                                            locationItem?.images.map(
                                              (it) => it?.id,
                                            )[1],
                                          )}
                                          alt=""
                                        />
                                        <TouchAppIcon
                                          sx={{
                                            position: "absolute",
                                            bottom: "5px",
                                            left: "5px",
                                            color: "white",
                                          }}
                                        />
                                      </Box>
                                      <CardContent
                                        sx={{
                                          pl: 0,
                                          pt: 1,
                                          pb: 1,
                                          display: "flex",
                                          flexDirection: "column",
                                          justifyContent: "flex-start",
                                          height: "100%",
                                          minWidth: 0,
                                        }}
                                      >
                                        <Typography
                                          fontSize={"12px"}
                                          color={"gray"}
                                        >
                                          {locationItem?.title}
                                        </Typography>
                                        <Typography
                                          fontSize={"10px"}
                                          color={"gray"}
                                          sx={{ pb: 1 }}
                                        >
                                          {locationItem?.address?.match(
                                            /\(([^)]+)\)/,
                                          )
                                            ? locationItem?.address?.match(
                                                /\(([^)]+)\)/,
                                              )[1]
                                            : null}
                                        </Typography>
                                        {/*<Grid item size={4}>*/}
                                        {/*    {*/}
                                        <Stack
                                          useFlexGap
                                          sx={{
                                            flexWrap: "nowrap",
                                            justifyContent: "flex-start",
                                            alignItems: "center",
                                            whiteSpace: "nowrap",
                                            overflowX: "auto",
                                            width: "100%",
                                            pr: 1,
                                            "&::-webkit-scrollbar": {
                                              display: "none",
                                            },
                                          }}
                                          direction={"row"}
                                          spacing={1}
                                        >
                                          {locationItem?.timeFrames?.map(
                                            (tf) => (
                                              <Stack
                                                direction={"row"}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  event.preventDefault();

                                                  if (
                                                    isPickedDateTime(
                                                      locationItem,
                                                      tf,
                                                    )
                                                  ) {
                                                    setServiceConfirm(
                                                      DEFAULT_SERVICE_CONFIRM_STATE,
                                                    );
                                                  } else {
                                                    _handleLocationTimeClick(
                                                      serviceDetailed
                                                        ?.serviceTemplate?.id,
                                                      locationItem?.id,
                                                      tf.from,
                                                    );
                                                  }
                                                }}
                                                data-test-id={"btn-time"}
                                                sx={{
                                                  flexShrink: 0,
                                                  border: `1px solid ${isPickedDateTime(locationItem, tf) ? "#76c6ff" : "#e3e3e3"}`,
                                                  backgroundColor: "#f3f3f3",
                                                  borderRadius: "25px",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                  p: "4x",
                                                  pt: "10px",
                                                  pb: "10px",
                                                  pl: "10px",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                <Typography
                                                  sx={{
                                                    fontSize: "12px",
                                                    mr: "2px",
                                                    flexWrap: "nowrap",
                                                  }}
                                                >
                                                  {getHourMinutes(tf.from) +
                                                    " - " +
                                                    getHourMinutes(tf.to)}
                                                </Typography>
                                                {isPickedDateTime(
                                                  locationItem,
                                                  tf,
                                                ) ? (
                                                  <CheckCircleIcon
                                                    sx={{
                                                      color: "#76c6ff",
                                                      fontSize: "24px",
                                                      mr: "4px",
                                                      ml: "4px",
                                                    }}
                                                  />
                                                ) : (
                                                  <AddIcon
                                                    sx={{
                                                      border:
                                                        "1px solid #a7a7a7",
                                                      mr: "4px",
                                                      ml: "4px",
                                                      borderRadius: "50%",
                                                      color: "#a7a7a7",
                                                      fontSize: "20px",
                                                    }}
                                                  />
                                                )}
                                              </Stack>
                                            ),
                                          )}
                                        </Stack>
                                        {/*//     }*/}
                                        {/*// </Grid>*/}
                                        {/*<Typography sx={{fontSize: '16px'}} align={'left'}> { item?.serviceTemplate?.title }</Typography>*/}
                                      </CardContent>
                                    </CardActionArea>
                                  </Box>
                                ))}
                              </>
                            ))}
                          itemsLoading={false}
                          closeDetailed={() => {
                            setLocationDetailed(null);
                          }}
                          itemDetailed={
                            locationDetailed ? (
                              <Card
                                elevation={1}
                                sx={{ display: "block", minHeight: "100vh" }}
                              >
                                <Box sx={{ minHeight: "100vh" }}>
                                  {/*<CardMedia*/}
                                  {/*    sx={{ height: '200px'}}*/}
                                  {/*    component="img"*/}
                                  {/*    // image={ getImage(locationDetailed?.images?.map(im => im.id)) }*/}
                                  {/*    image={  getImageV2(locationDetailed?.images.map(it => it?.id)[1]) }*/}
                                  {/*    alt=""*/}
                                  {/*/>*/}
                                  <SwipableImageCarousel
                                    images={
                                      locationDetailed?.images.slice(1) || []
                                    }
                                    aspectRatio={"3/2"}
                                    getImage={getImage}
                                    imageUrls={locationDetailed?.images
                                      ?.slice(1)
                                      .map((it) => getImageV2(it?.id))}
                                    navigation={"fraction"}
                                  />
                                  <CardContent
                                    sx={{
                                      position: "relative",
                                      marginTop: "-25px",
                                      backgroundColor: "#fff",
                                      borderTopLeftRadius: "25px",
                                      borderTopRightRadius: "25px",
                                    }}
                                  >
                                    <Typography
                                      align="center"
                                      sx={{ pb: 2, fontSize: "20px" }}
                                    >
                                      {" "}
                                      {locationDetailed?.title}{" "}
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: "12px", color: "gray" }}
                                      align="center"
                                    >
                                      {" "}
                                      {locationDetailed?.description}{" "}
                                    </Typography>
                                    <Typography
                                      sx={{ pt: 6, pb: 1, fontSize: "16px" }}
                                      align="left"
                                    >
                                      {" "}
                                      Адрес{" "}
                                    </Typography>
                                    <Typography
                                      sx={{ fontSize: "12px" }}
                                      align="left"
                                    >
                                      {" "}
                                      {locationDetailed?.address}{" "}
                                    </Typography>
                                    <Box sx={{ mt: 2 }}>
                                      <ItemsListV2
                                        items={[
                                          <Box sx={{ position: "relative" }}>
                                            <Box
                                              sx={{
                                                // aspectRatio: '3/2',
                                                width: "100%",
                                                borderRadius: "15px",
                                                mt: 2,
                                              }}
                                              onClick={() => {
                                                setMapDetailed(true);
                                              }}
                                              component={"img"}
                                              alt={""}
                                              // src={ getImage(locationDetailed?.images?.map(im => im.id)) }
                                              src={
                                                locationDetailed?.images?.map(
                                                  (it) => getImageV2(it?.id),
                                                )[0]
                                              }
                                            />
                                            <ZoomOutMapIcon
                                              onClick={() =>
                                                setMapDetailed(true)
                                              }
                                              sx={{
                                                "&:hover": {
                                                  cursor: "pointer",
                                                  backgroundColor: "#000",
                                                  color: "#fff",
                                                },
                                                p: "5px",
                                                backgroundColor: "#fff",
                                                borderRadius: "12px",
                                                zIndex: 1,
                                                position: "absolute",
                                                top: "25px",
                                                right: "10px",
                                              }}
                                            />
                                          </Box>,
                                        ]}
                                        itemsLoading={false}
                                        closeDetailed={() => {
                                          setMapDetailed(null);
                                        }}
                                        itemDetailed={
                                          mapDetailed ? (
                                            <Box
                                              sx={{
                                                width: "100vw",
                                                height: "100vh",
                                              }}
                                            >
                                              <iframe
                                                src={locationDetailed?.mapUrl}
                                                width="100%"
                                                height="100%"
                                                frameBorder="0"
                                              ></iframe>
                                            </Box>
                                          ) : null
                                        }
                                      />
                                    </Box>
                                  </CardContent>
                                </Box>
                              </Card>
                            ) : null
                          }
                        />
                      </CardContent>
                    </Box>
                    <Box
                      sx={{
                        borderTop: "1px solid #e3e3e3",
                      }}
                    >
                      <Stack
                        direction={"row"}
                        sx={{
                          p: 2,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        spacing={2}
                      >
                        <Stack
                          sx={{
                            flex: "1 1 50%",
                            maxWidth: "300px",
                          }}
                        >
                          <Typography sx={{ fontSize: "16px" }} align={"left"}>
                            {" "}
                            {serviceDetailed?.serviceTemplate?.price}{" "}
                            <span>&#8381;</span>
                          </Typography>
                          {serviceConfirm?.serviceId ? (
                            <Typography
                              sx={{ fontSize: "10px", color: "gray" }}
                            >
                              {" "}
                              {serviceConfirm.text}{" "}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: "8px", color: "gray" }}>
                              Выберите время для любой из доступных дат
                            </Typography>
                          )}
                        </Stack>
                        {serviceConfirm?.serviceId ? (
                          <Button
                            variant={"contained"}
                            loading={serviceConfirm.inProgress}
                            loadingIndicator={
                              <Stack
                                direction={"row"}
                                spacing={1}
                                sx={{
                                  justifyContent: "center",
                                  alignItems: "center",
                                }}
                              >
                                <Box>
                                  <CircularProgress
                                    size={"20px"}
                                    color={"gray"}
                                  />
                                </Box>
                                <Typography>Забронировать</Typography>
                              </Stack>
                            }
                            size={"large"}
                            onClick={() => {
                              _handleServiceBook(serviceConfirm);
                            }}
                          >
                            Забронировать
                          </Button>
                        ) : (
                          <Button
                            variant={"contained"}
                            onClick={() => {
                              datePlaceSection.current?.scrollIntoView({
                                behavior: "smooth",
                              });
                            }}
                            sx={{
                              flex: "1 1 50%",
                              maxWidth: "300px",
                            }}
                          >
                            Выбрать
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </Card>
                ) : null
              }
            />
            {false ? (
              <ItemsList
                items={groupServicesByServiceTemplate(
                  services.data,
                  serviceTemplates.data,
                  locations.data,
                )}
                getItemId={(item) => item?.serviceTemplate?.id}
                getItemTitle={(item) => item?.serviceTemplate?.title}
                getItemDescription={(item) => item?.serviceTemplate.description}
                getItemImages={(item) => {
                  return getImage(
                    item?.serviceTemplate?.images?.map((im) => im.id),
                  );
                }}
                getShortComponent={(item) => {
                  return (
                    <Grid container columnSpacing={1} rowSpacing={1}>
                      <Grid item xs={12}>
                        <Typography sx={{ fontSize: "16px" }} align={"left"}>
                          {item?.serviceTemplate?.title}
                        </Typography>
                      </Grid>
                    </Grid>
                  );
                }}
                getDetailedComponent={(item) => {
                  const serviceTemplateItem = item;

                  return (
                    <>
                      <Typography sx={{ pt: 2 }} align={"left"}>
                        Место и время
                      </Typography>
                      <ItemsList
                        items={serviceTemplateItem?.locations}
                        getItemId={(item) => item?.id}
                        getItemTitle={(item) => item?.title}
                        getItemDescription={(item) => (
                          <Stack>
                            <Typography align={"left"}>
                              {item?.address}
                            </Typography>
                            <Typography align={"left"}>
                              {item?.description}
                            </Typography>
                          </Stack>
                        )}
                        getItemImages={(item) => {
                          return getImage(item?.images?.map((im) => im.id));
                        }}
                        getShortComponent={(item) => {
                          const locationItem = item;
                          return (
                            <Grid item size={4}>
                              {item?.timeFrames.map((tf) => (
                                <Chip
                                  size={"small"}
                                  label={
                                    getHourMinutes(tf.from) +
                                    " - " +
                                    getHourMinutes(tf.to)
                                  }
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    _handleLocationTimeClick(
                                      serviceTemplateItem?.serviceTemplate?.id,
                                      locationItem?.id,
                                      tf.from,
                                    );
                                  }}
                                ></Chip>
                              ))}
                            </Grid>
                          );
                        }}
                        getDetailedComponent={(item) => {
                          return <div></div>;
                        }}
                      />
                    </>
                  );
                }}
              />
            ) : null}
          </Box>
        </Grid>
      </Grid>
      {/* all helper components */}
      <FilterInfoV2
        open={filterInfo.open}
        handleClose={_handleFilterModalClose}
      >
        <Stack
          sx={{
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ p: 2, pt: 4, pb: 4 }}>
            <Typography
              align={"center"}
              sx={{ fontWeight: "bold", fontSize: "22px" }}
            >
              Фильтры
            </Typography>
          </Box>
          <Box sx={{ overflowY: "auto", height: "100%" }}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              sx={{
                pt: 1,
                pb: 1,
                pr: 1,
              }}
            >
              <CalendarMonthIcon sx={{ p: 2, color: "rgb(0 0 0/0.75)" }} />
              <Stack
                direction={"row"}
                sx={{
                  width: "100%",
                  // borderBottom: '1px solid rgb(0 0 0 / 0.1)',
                  pb: 1,
                }}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Stack
                  sx={{
                    flex: "1 1 auto",
                    justifyContent: "space-between",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "rgb(0 0 0 / 0.01)",
                    },
                  }}
                  direction={"row"}
                  onClick={() => {
                    setFilterInfo((prevState) => ({
                      ...prevState,
                      calendar: {
                        ...prevState.calendar,
                        openFrom: !prevState?.calendar?.openFrom,
                        openTo: false,
                      },
                    }));
                    setShowCalendar((prevState) => !prevState);
                  }}
                >
                  <Stack>
                    <Typography
                      fontSize={"12px"}
                      sx={{ color: "rgb(0 0 0/ 0.7)" }}
                    >
                      С даты
                    </Typography>
                    <Typography>
                      {getDay(
                        _getValueFromFilter(
                          "timeFrameFrom",
                          filterInfo?.filters,
                        ),
                      )}
                    </Typography>
                  </Stack>
                  <ArrowForwardIosIcon
                    sx={{
                      p: 2,
                      color: "rgb(0 0 0/0.75)",
                      fontSize: "16px",
                      rotate: filterInfo.calendar.openFrom ? "90deg" : "none",
                    }}
                    // onClick={() => {
                    //     setFilterInfo(prevState => ({...prevState, calendar: {...prevState.calendar, openFrom: !prevState?.calendar?.openFrom, openTO: false}}))
                    //     setShowCalendar(prevState => !prevState)
                    // }}
                  />
                </Stack>
                <Stack
                  sx={{
                    flex: "1 1 auto",
                    justifyContent: "space-between",
                    "&:hover": {
                      cursor: "pointer",
                      backgroundColor: "rgb(0 0 0 / 0.01)",
                    },
                  }}
                  direction={"row"}
                  onClick={() => {
                    setFilterInfo((prevState) => ({
                      ...prevState,
                      calendar: {
                        ...prevState.calendar,
                        openTo: !prevState?.calendar?.openTo,
                        openFrom: false,
                      },
                    }));
                    setShowCalendar((prevState) => !prevState);
                  }}
                >
                  <Stack>
                    <Typography
                      fontSize={"12px"}
                      sx={{ color: "rgb(0 0 0/ 0.7)" }}
                    >
                      По дату
                    </Typography>
                    <Typography>
                      {getDay(
                        _getValueFromFilter("timeFrameTo", filterInfo?.filters),
                      )}
                    </Typography>
                  </Stack>
                  <ArrowForwardIosIcon
                    sx={{
                      p: 2,
                      color: "rgb(0 0 0/0.75)",
                      fontSize: "16px",
                      rotate: filterInfo.calendar.openTo ? "90deg" : "none",
                    }}
                    // onClick={() => {
                    //     setFilterInfo(prevState => ({...prevState, calendar: {...prevState.calendar, open: !prevState?.calendar?.open}}))
                    //     setShowCalendar(prevState => !prevState)
                    // }}
                  />
                </Stack>
              </Stack>
              {/*<ArrowForwardIosIcon*/}
              {/*    sx={{*/}
              {/*        p: 2,*/}
              {/*        color: 'rgb(0 0 0/0.75)',*/}
              {/*        fontSize: '16px',*/}
              {/*        rotate: filterInfo.calendar.open ? '90deg': 'none'*/}
              {/*    }}*/}
              {/*                     onClick={() => {*/}
              {/*                         setFilterInfo(prevState => ({...prevState, calendar: {...prevState.calendar, open: !prevState?.calendar?.open}}))*/}
              {/*                         setShowCalendar(prevState => !prevState)*/}
              {/*                     }}*/}
              {/*/>*/}
            </Stack>
            {filterInfo?.calendar?.openFrom ? (
              <LocalizationProvider
                dateAdapter={AdapterLuxon}
                adapterLocale={"ru"}
              >
                <StaticDatePicker
                  onChange={(newDate) => {
                    handleDatePickV2(newDate, "from");
                  }}
                  onClose={() => {
                    setFilterInfo((prevState) => ({
                      ...prevState,
                      calendar: { ...prevState?.calendar, openFrom: false },
                    }));
                  }}
                  views={["month", "day"]}
                  value={DateTime.fromSeconds(
                    _getValueFromFilter("timeFrameFrom", filterInfo?.filters),
                  )}
                  disablePast
                  maxDate={DateTime.fromSeconds(
                    _getValueFromFilter("timeFrameTo", filterInfo?.filters),
                  )}
                  slotProps={{
                    toolbar: {
                      hidden: true,
                    },
                  }}
                  slots={{
                    actionBar: () => null,
                  }}
                />
              </LocalizationProvider>
            ) : null}
            {filterInfo?.calendar?.openTo ? (
              <LocalizationProvider
                dateAdapter={AdapterLuxon}
                adapterLocale={"ru"}
              >
                <StaticDatePicker
                  onChange={(newDate) => {
                    handleDatePickV2(newDate, "to");
                  }}
                  onClose={() => {
                    setFilterInfo((prevState) => ({
                      ...prevState,
                      calendar: { ...prevState?.calendar, openTo: false },
                    }));
                  }}
                  views={["month", "day"]}
                  value={DateTime.fromSeconds(
                    _getValueFromFilter("timeFrameTo", filterInfo?.filters),
                  )}
                  disablePast
                  minDate={DateTime.fromSeconds(
                    _getValueFromFilter("timeFrameFrom", filterInfo?.filters),
                  )}
                  slotProps={{
                    toolbar: {
                      hidden: true,
                    },
                  }}
                  slots={{
                    actionBar: () => null,
                  }}
                />
              </LocalizationProvider>
            ) : null}
          </Box>

          <Box sx={{ p: 2 }}>
            <Stack
              direction={"row"}
              sx={{ justifyContent: "center" }}
              spacing={2}
            >
              <Button
                variant={"outlined"}
                onClick={() => {
                  setFilterInfo({ ...DEFAULT_FILTER_INFO_STATE, open: true });
                }}
                sx={{
                  flex: "1 1 auto",
                  maxWidth: "300px",
                }}
              >
                Сбросить
              </Button>
              <Button
                variant={"contained"}
                onClick={() => {
                  setFilters([...filterInfo.filters]);
                  setServices((prevState) => ({
                    ...prevState,
                    isLoaded: false,
                  }));
                  setFilterInfo((prevState) => ({ ...prevState, open: false }));
                }}
                sx={{
                  flex: "1 1 auto",
                  maxWidth: "300px",
                }}
              >
                Применить
              </Button>
            </Stack>
          </Box>
        </Stack>
      </FilterInfoV2>
      <ServiceConfirm
        open={serviceConfirm.open}
        handleClose={() => {
          setServiceConfirm((prevState) => ({ ...prevState, open: false }));
        }}
        getPrimaryContent={() => {
          return (
            <Stack>
              <Typography sx={{ pb: 1, color: "gray" }}>
                {" "}
                Подтвердить бронирование{" "}
              </Typography>
              <Typography> {serviceConfirm.text} </Typography>
              <Divider sx={{ pt: 1, pb: 1 }} />
              <Button
                loading={serviceConfirm.inProgress}
                loadingIndicator={
                  <Stack
                    direction={"row"}
                    spacing={1}
                    sx={{ justifyContent: "center", alignItems: "center" }}
                  >
                    <Box>
                      <CircularProgress size={"20px"} color={"gray"} />
                    </Box>
                    <Typography>Забронировать</Typography>
                  </Stack>
                }
                size={"large"}
                onClick={() => {
                  _handleServiceBook(serviceConfirm);
                }}
              >
                Забронировать
              </Button>
            </Stack>
          );
        }}
        getSecondaryContent={() => {
          return (
            <Typography
              align={"center"}
              onClick={() => {
                setServiceConfirm(DEFAULT_SERVICE_CONFIRM_STATE);
              }}
            >
              Отменить
            </Typography>
          );
        }}
      />
      <ServiceConfirmStatus
        open={serviceConfirmStatus.open}
        handleClose={() => {
          setServiceDetailed(null);
          setLocationDetailed(null);
          setServices({ isLoaded: false });
          setServiceConfirmStatus(DEFAULT_SERVICE_CONFIRM_STATUS_STATE);
        }}
        getPrimaryContent={() => {
          if (serviceConfirmStatus.isError) {
            return <Typography>Что-то пошло не так</Typography>;
          }

          return (
            <Typography>
              Сервис успешно забронирован. Отменить или отредактировать его
              можно будет в своем аккаунте{" "}
            </Typography>
          );
        }}
        getSecondaryContent={() => {
          return (
            <Typography
              align={"center"}
              onClick={() => {
                setServiceDetailed(null);
                setLocationDetailed(null);
                setServices({ isLoaded: false });
                setServiceConfirmStatus(DEFAULT_SERVICE_CONFIRM_STATUS_STATE);
              }}
            >
              ОК
            </Typography>
          );
        }}
      />
      <RedirectToAuth
        open={redirectToAuth?.open}
        handleClose={() => {
          setRedirectToAuth(DEFAULT_REDIRECT_TO_AUTH_STATE);
        }}
        getPrimaryContent={() => {
          return (
            <Stack>
              <Typography align={"center"} sx={{ pb: 1, color: "gray" }}>
                Подтверждение номера телефона
              </Typography>
              <Typography align={"center"}>
                {" "}
                Для бронирования сервиса необходимо подтвердить номер
                телефона{" "}
              </Typography>
              <Divider sx={{ pt: 1, pb: 1 }} />
              <Button
                size={"large"}
                onClick={() => {
                  navigate("/account");
                }}
              >
                Подтвердить
              </Button>
            </Stack>
          );
        }}
        getSecondaryContent={() => {
          return (
            <Typography
              align={"center"}
              onClick={() => {
                setRedirectToAuth(DEFAULT_REDIRECT_TO_AUTH_STATE);
              }}
            >
              Отмена
            </Typography>
          );
        }}
      />
    </App>
  );

  //   old code
  const { gsFilters, setGSFilters } = useContext(GlobalDataContext);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [phantomServicesGrouped, setPhantomServicesGrouped] = useState({
    isLoaded: false,
    data: [],
  });
  const [allLocations, setAllLocations] = useState({
    isLoaded: false,
    data: [],
  });
  const [allServices, setAllServices] = useState({
    isLoaded: false,
    data: [],
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialService = () => {
    const passedService = searchParams.get("service");

    return passedService;
  };

  const getInitialDate = () => {
    const passedDate = searchParams.get("date");

    let showedDate = DateTime.fromISO(passedDate, { zone: "utc" });

    return showedDate.isValid && showedDate > DateTime.now()
      ? showedDate
      : DateTime.now();
  };

  const DEFAULT_SFILTERS = (() => {
    let initialFilters = [{ type: "date", value: getInitialDate() }];

    if (getInitialService()) {
      initialFilters.push({ type: "service", value: getInitialService() });
    }

    return initialFilters;
  })();

  // todo: uncomment
  // const [ sFilters, setSFilters ] = useState(DEFAULT_SFILTERS);
  const sFilters = gsFilters;
  const setSFilters = setGSFilters;

  // new functions
  const groupPhantomServices = (
    phantomServices,
    serviceTemplates,
    allLocations,
  ) => {
    return phantomServices.reduce((acc, curr) => {
      const idx = acc.findIndex(
        (it) => it.serviceTemplate.id === curr.serviceTemplate.id,
      );

      if (idx != -1) {
        const locationIdx = acc[idx].locations.findIndex(
          (l) => l.id == curr.location.id,
        );

        if (locationIdx != -1) {
          acc[idx].locations[locationIdx].timeFrames.push(curr.timeFrame);
        } else {
          acc[idx].locations = [fillById(curr.location.id, allLocations)];
          acc[idx][0].timeFrames = [curr.timeFrame];
        }
      } else {
        acc.push({
          serviceTemplate: fillById(curr.serviceTemplate.id, serviceTemplates),
          locations: [
            {
              ...fillById(curr.location.id, allLocations),
              timeFrames: [curr.timeFrame],
            },
          ],
        });
      }

      return acc;
    }, []);
  };

  const fillById = (id, arr = []) => {
    const ix = arr.findIndex((it) => it.id == id);
    if (ix != -1) {
      return arr[ix];
    }

    return { id };
  };

  const getValueFromFilterReadable = (type) => {
    let value;

    if (type == "location") {
      value = getValueFromFilter(type)?.title;
    } else if (type == "date") {
      value = getValueFromFilter(type)
        ?.setLocale("ru")
        ?.toLocaleString({ month: "short", day: "numeric" })
        .slice(0, -1);
    } else if (type == "service") {
      value = getValueFromFilter(type)?.serviceTemplate?.title;
    } else if (type == "time") {
      value = getHourMinutes(getValueFromFilter(type));
    }

    return value ? value : " ";
  };

  const getValueFromFilter = (type) => {
    if (!sFilters || !sFilters.filter((f) => f.type == type).length) {
      return null;
    }

    return sFilters.filter((f) => f.type == type)[0].value;
  };

  const handleServicePick = (phantomService) => {
    setSFilters((prevState) => {
      const nextState = [...prevState];
      let ix = nextState.findIndex((fltr) => fltr.type == "service");

      if (ix != -1) {
        if (
          nextState[ix].value.serviceTemplate.id ==
          phantomService?.serviceTemplate?.id
        ) {
          return [...nextState];
        }
        nextState.splice(ix, 1);
      }

      ix = nextState.findIndex((fltr) => fltr.type == "location");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }

      ix = nextState.findIndex((fltr) => fltr.type == "time");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }

      if (phantomService == null) {
        return nextState;
      }

      return [...nextState, { type: "service", value: phantomService }];
    });

    if (phantomService != null) {
      // todo open detailed view of service
      setItemDetailedPassed(phantomService);
    }
  };

  const getCentralChip = () => {
    const totalFiltersNumber = ["service", "location", "date"]
      .map((type) => getFromFiltersReadable(type))
      .filter((value) => {
        return value != "";
      }).length;

    const onlyDate = getChipsFromFilters("date") && totalFiltersNumber === 1;
    const dateService =
      getChipsFromFilters("date") &&
      getChipsFromFilters("service") &&
      totalFiltersNumber === 2;
    const dateServiceLocation =
      getChipsFromFilters("date") &&
      getChipsFromFilters("service") &&
      getChipsFromFilters("location") &&
      totalFiltersNumber === 3;

    let element = null;

    if (totalFiltersNumber === 0) {
      return null;
    }

    if (onlyDate) {
      element = (
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
          }}
          display={"column"}
        >
          <Box
            sx={{
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
            }}
          >
            <Typography fontSize={"18px"} sx={{ color: "gray" }}>
              <CalendarMonthIcon
                sx={{ fontSize: "12px", fontWeight: "bold", pr: "2px" }}
              />
              {getValueFromFilterReadable("date")}
            </Typography>
          </Box>
        </Stack>
      );
    } else if (dateService) {
      element = (
        <Stack
          sx={{ alignItems: "center", display: "flex" }}
          display={"column"}
        >
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={"16px"} align={"center"}>
              {getValueFromFilterReadable("service")}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              fontSize={"12px"}
              sx={{
                color: "gray",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CalendarMonthIcon
                sx={{ fontSize: "10px", fontWeight: "bold", pr: "2px" }}
              />
              {getValueFromFilterReadable("date")}
            </Typography>
          </Box>
        </Stack>
      );
    } else if (dateServiceLocation) {
      element = (
        <Stack
          sx={{ alignItems: "center", display: "flex" }}
          display={"column"}
        >
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={"16px"} align={"center"}>
              {getValueFromFilterReadable("service")}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              fontSize={"12px"}
              sx={{
                color: "gray",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CalendarMonthIcon
                sx={{ fontSize: "10px", fontWeight: "bold", pr: "2px" }}
              />
              {getValueFromFilterReadable("date")}
              <span>&nbsp;&middot;&nbsp;</span>
              {getValueFromFilterReadable("time")}
              <span>&nbsp;&middot;&nbsp;</span>
              {getValueFromFilterReadable("location")}
            </Typography>
          </Box>
        </Stack>
      );
    }

    return (
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          borderRadius: "30px",
          border: "1px solid #ddd",
          justifyContent: "center",
          alignItems: "center",
          p: "5px",
          height: "40px",
          boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
          width: "100%",
        }}
        onClick={() => {
          setPickedServiceInfo({ open: true });
        }}
      >
        {element}
      </Box>
    );
  };

  // old functions

  // const getLocations = () => {
  //     const pickedServices = sFilters.filter(f => f.type == 'service');
  //
  //     if (!pickedServices.length) return [];
  //
  //
  //     return phantomServicesGrouped.data.filter(ps => ps.serviceTemplateId == pickedServices[0].value)[0].locations
  //         .map(loc => {
  //             const ix = allLocations.data.findIndex(l => l.id == loc.id);
  //             if (ix != -1) {
  //                 loc.imageIds = allLocations.data[ix].imageIds;
  //                 loc.title = allLocations.data[ix].title;
  //                 loc.description = allLocations.data[ix].description;
  //             }
  //
  //             return loc;
  //         })
  // }

  const getFromFiltersReadable = (type) => {
    const list = sFilters.filter((f) => f.type == type);

    if (!list.length) {
      return "";
    }

    let value;

    if (type == "location") {
      value = getValueFromFilter("location")?.title;
    } else if (type == "date") {
      value = list[0].value
        .setLocale("ru")
        .toLocaleString({ month: "short", day: "numeric" });
    } else if (type == "service") {
      value =
        phantomServicesGrouped.data.filter(
          (p) => p.serviceTemplateId == list[0].value,
        )[0] &&
        phantomServicesGrouped.data.filter(
          (p) => p.serviceTemplateId == list[0].value,
        )[0].title;
    }

    return value ? value : " ";
  };

  const drawerBleeding = 300;

  //todo: move to another file
  // const style = {
  //     position: 'absolute',
  //     top: '50%',
  //     left: '50%',
  //     transform: 'translate(-50%, -50%)',
  //     width: 400,
  //     bgcolor: 'background.paper',
  //     boxShadow: 24,
  //     p: 4,
  //     borderRadius: 2
  // };

  // const filters = [
  //     { type: 'date', value: ''},
  //     { type: 'category', value: 'massage'},
  //     { type: 'product', value: 'back-massage'},
  //     { type: 'city', value: 'moscow'},
  //     { type: 'location', value: 'spasokukozkiy' }
  // ];

  // const phantomServices = BSERVICES.filter((product) => product);

  // const handleLocationDetailsClick = (id) => {
  //     setShowLocationDetails({id: id});
  // }

  // const getFilterValueOfType = (filters, type) => {
  //     const filtered = filters.filter(fltr => fltr.type == type);
  //
  //     return filtered.length ? filtered[0].value : null;
  // }

  // const getFilteredLocations = () => {
  //     return LOCATIONS;
  // }

  // const handleLocationClick = (id) => {
  //     setSFilters((prevState => {
  //         const nextState = [...prevState];
  //         const ix = nextState.findIndex(fltr => fltr.type == 'location');
  //         if (ix != -1) {
  //             nextState.splice(ix, 1);
  //         }
  //
  //         const finalState =  [...nextState, { type: 'location', value: id}];
  //         return finalState;
  //     }))
  // }

  const handleProductDelete = () => {
    setSFilters((prevState) => {
      const nextState = [...prevState];
      let ix = nextState.findIndex((fltr) => fltr.type == "service");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }
      ix = nextState.findIndex((fltr) => fltr.type == "time");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }
      ix = nextState.findIndex((fltr) => fltr.type == "location");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }

      return [...nextState];
    });
  };

  const handleLocationDelete = () => {
    setSFilters((prevState) => {
      const nextState = [...prevState];
      let ix = nextState.findIndex((fltr) => fltr.type == "location");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }

      ix = nextState.findIndex((fltr) => fltr.type == "time");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }

      return [...nextState];
    });
  };

  const handleDatePick = (newDate) => {
    setSFilters((prevState) => {
      const nextState = [...prevState];
      const ix = nextState.findIndex((fltr) => fltr.type == "date");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }

      return [...nextState, { type: "date", value: newDate }];
    });
    setShowCalendar(false);
  };

  const handleDateDelete = () => {
    setSFilters((prevState) => {
      const nextState = [...prevState];
      const ix = nextState.findIndex((fltr) => fltr.type == "date");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }

      return [...nextState];
    });
  };

  const getFilterMenuItem = () => {
    return (
      <Box>
        <Stack
          direction={"row"}
          alignItems={"center"}
          sx={{
            pt: 1,
            pb: 1,
            pr: 1,
          }}
        >
          <span
            class={
              info.data.highlight.filter((f) => f == "service").length
                ? "shake"
                : ""
            }
          >
            <SpaIcon sx={{ p: 2, color: "rgb(0 0 0/0.75)" }} />
          </span>
          <Stack
            direction={"row"}
            sx={{
              width: "100%",
              borderBottom: "1px solid rgb(0 0 0 / 0.1)",
              pb: 1,
              "&:hover": {
                cursor: "pointer",
                backgroundColor: "rgb(0 0 0 / 0.01)",
              },
            }}
            onClick={() => {
              setInfo((prevState) => ({
                ...prevState,
                data: { ...prevState.data, openSX: false, openSM: false },
              }));
              setDrawerSection("services");
              setDrawerOpen(true);

              setShakeServices(true);
              setTimeout(() => {
                setShakeServices(false);
              }, 2000);
            }}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Stack>
              <Typography fontSize={"12px"} sx={{ color: "rgb(0 0 0/ 0.7)" }}>
                Процедура
              </Typography>
              <Typography>{getFromFiltersReadable("service")}</Typography>

              {getFromFiltersReadable("service") ? null : (
                <Typography fontSize={"10px"} sx={{ color: "rgb(0 0 0/0.7)" }}>
                  Не выбрана
                </Typography>
              )}
            </Stack>
            <ArrowForwardIosIcon fontSize={"10px"} />
          </Stack>
        </Stack>
      </Box>
    );
  };

  const handleTimeDelete = () => {
    setSFilters((prevState) => {
      const nextState = [...prevState];
      let ix = nextState.findIndex((fltr) => fltr.type == "time");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }

      ix = nextState.findIndex((fltr) => fltr.type == "location");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }

      return [...nextState];
    });
  };

  //todo: rename to handleFiltersOpen
  const handleFilterModalOpen = () => {
    setInfoData({
      openXS: true,
      openSM: true,
      content: "FILTER",
      highlight: [],
    });
  };

  const handleFilterModalClose = () => {
    setFilterModalOpen(false);
    setShowCalendar(false);
  };

  const [fileterModalOpen, setFilterModalOpen] = useState(false);
  const [showLocationDetails, setShowLocationDetails] = useState(null);

  const getLocationDetails = () => {
    return showLocationDetails;
  };

  const getChipsFromFilters = () => {
    const onDeleteHandler = {
      date: false,
      time: handleTimeDelete,
      location: handleLocationDelete,
      service: handleProductDelete,
    };

    let newSFilters = [];
    for (let key in onDeleteHandler) {
      for (let i = 0; i < sFilters.length; i++) {
        if (sFilters[i].type == key) {
          newSFilters.push(sFilters[i]);
        }
      }
    }

    return newSFilters.map((filter) => {
      // let label = filter.value.toString();
      let label = "DEFAULT";

      if (filter.type == "date") {
        label = filter.value.setLocale("ru").toLocaleString({
          month: "short",
          day: "numeric",
        });
      } else if (filter.type == "time") {
        label = filter.value;
      } else if (filter.type == "service") {
        label = filter.value?.serviceTemplate?.title;
        // label = phantomServicesGrouped.data.filter(p => p.serviceTemplateId == filter.value).length && phantomServicesGrouped.data.filter(p => p.serviceTemplateId == filter.value)[0].title;
      } else if (filter.type == "location") {
        label = filter.value?.title;
        // label = LOCATIONS.filter(l => l.id == filter.value)[0].title
      }

      return (
        <Chip
          size={"small"}
          label={label}
          onDelete={onDeleteHandler[filter.type]}
        ></Chip>
      );
    });
  };

  const handleServiceBook = async () => {
    const date = sFilters.filter((f) => f.type == "date").length;
    const time = sFilters.filter((f) => f.type == "time").length;
    const service = sFilters.filter((f) => f.type == "service").length;
    const location = sFilters.filter((f) => f.type == "location").length;

    if (date && time && service && location) {
      if (!phoneNumber) {
        // setInfoSmall(prevState =>
        //     ({...prevState, data: {...prevState.data, open: true, content: 'LOGIN', status: 'VERIFY_PHONE' }}))
        setSmsOpen(true);
        return;
      }

      try {
        const resp = await axios.put(`api/v1/services`, {
          id: getValueFromFilter("service")?.id,
          client: {
            id: userId,
          },
        });
      } catch (err) {
        throw err;
      }

      setSFilters(DEFAULT_SFILTERS);
      setInfoSmall((prevState) => ({
        ...prevState,
        data: {
          ...prevState.data,
          open: true,
          content: "SERVICE_BOOK_SUCCESS",
        },
      }));
    } else {
      const highlight = [];
      if (!date) highlight.push("date");
      if (!time) highlight.push("time");
      if (!service) highlight.push("service");
      if (!location) highlight.push("location");

      setPickedServiceInfo({
        open: true,
        highlight: highlight,
      });

      // setInfoData({
      //     openXS: true,
      //     openSM: true,
      //     content: 'FILTER',
      //     highlight: highlight
      // })
    }
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: "#76c6ff",
      },
    },
  });
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Checks if screen is 'sm' or smalle

  // states
  const [mapViewOnMobile, setMapViewOnMobile] = useState("list"); // map, list
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSection, setDrawerSection] = useState("services");
  const [showCalendar, setShowCalendar] = useState(false);

  const [shakeLocations, setShakeLocations] = useState(false);
  const [shakeServices, setShakeServices] = useState(false);
  const [appointment, setAppointment] = useState({
    data: {
      date: null,
      time: null,
      location: null,
    },
  });

  const handleLocationTimeClick = (time, location, serviceId) => {
    setSFilters((prevState) => {
      const nextState = [...prevState];
      const ix = nextState.findIndex((fltr) => fltr.type == "time");
      if (ix != -1) {
        nextState.splice(ix, 1);
      }

      const ixLocation = nextState.findIndex((f) => f.type == "location");
      if (ixLocation != -1) {
        nextState.splice(ixLocation, 1);
      }

      const ixServiceId = nextState.findIndex((f) => f.type == "serviceId");
      if (ixLocation != -1) {
        nextState.splice(ixServiceId, 1);
      }

      // handleLocationTimeClick(serviceTemplateItem?.id, item?.id, tf.from);
      return [
        ...nextState,
        { type: "time", value: time },
        { type: "location", value: location },
        { type: "serviceId", value: serviceId },
      ];
    });

    infoActions.handleClose();
  };

  const disableCalendarDates = (date) => {
    return date < DateTime.now() && date > DateTime.now().plus({ days: 14 });
  };

  const [info, setInfo] = useState({
    data: {
      open: false,
      openXS: false,
      openSM: false,
      content: null,
    },
  });

  const [shake, setShake] = useState([]);

  const setInfoData = (data) => {
    setInfo((prevState) => ({
      ...prevState,
      data: { ...prevState.data, ...data },
    }));
    setTimeout(() => {
      setInfo((prevState) => ({
        ...prevState,
        data: { ...prevState.data, highlight: [] },
      }));
    }, 2000);
  };

  const infoActions = {
    handleClose: () => {
      setInfo((prevState) => ({
        ...prevState,
        data: { ...prevState.data, openXS: false, openSM: false },
      }));
      setShowLocationDetails(false);
      // setShowProductDetails(false);
    },
    handleOpen: () => {
      setInfo((prevState) => ({
        ...prevState,
        data: { ...prevState.data, openXS: true, openSM: true },
      }));
    },
  };

  const [smsOpen, setSmsOpen] = useState(false);
  const [itemDetailedPassed, setItemDetailedPassed] = useState(null);

  const lightTheme = createTheme(getLPTheme("light"));
  const locRef = useRef("test");
  const [pickedServiceInfo, setPickedServiceInfo] = useState({
    open: false,
  });

  // todo move
  const [infoSmall, setInfoSmall] = useState({
    isLoaded: true,
    data: { open: false, content: "" },
  });

  return ret;
}

export default MainScreen;
